import { useCallback, useState, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import OpenIncidentsWmrcReportTable from './Table/OpenIncidentsWmrcReportTable'
import { useQuery } from '@tanstack/react-query'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'

const formatPayloadDates = (payload) => {
    if (!payload) return payload
    const formattedPayload = { ...payload }
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate)
        if (!isNaN(startDate))
            formattedPayload.startDate = startDate.toISOString().split('T')[0]
    }
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate)
        if (!isNaN(endDate))
            formattedPayload.endDate = endDate.toISOString().split('T')[0]
    }
    return formattedPayload
}

const OpenIncidentsWmrcReport = () => {
    const location = useLocation()
    const { token } = useSelector((state) => state?.auth)

    const [searchParams] = useSearchParams();

  // Initialize payload from location.state, sessionStorage, or URL query params
  const [payload, setPayload] = useState(() => {
    if (location.state) return location.state;

    const stored = sessionStorage.getItem("openIncidentsWmrcPayload");
    if (stored) return JSON.parse(stored);

    const params = Object.fromEntries([...searchParams]);
    return Object.keys(params).length ? params : null;
  });

  const [formattedPayload, setFormattedPayload] = useState(() =>
    formatPayloadDates(payload)
  );

  const storedTableOptions =
    JSON.parse(sessionStorage.getItem("openIncidentsWmrcTableOptions")) || {
      rowPerPages: 10,
      activePage: 0,
    };
  const storedSort =
    JSON.parse(sessionStorage.getItem("openIncidentsWmrcSort")) || null;

  const [tableOptions, setTableOptions] = useState(storedTableOptions);
  const [sort, setSort] = useState(storedSort);

  // Sync table options and sort with sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "openIncidentsWmrcTableOptions",
      JSON.stringify(tableOptions)
    );
  }, [tableOptions]);

  useEffect(() => {
    sessionStorage.setItem("openIncidentsWmrcSort", JSON.stringify(sort));
  }, [sort]);

  // Sync payload to sessionStorage when location.state or URL params change
  useEffect(() => {
    if (location.state) {
      const prevStored = sessionStorage.getItem("openIncidentsWmrcPayload");
      const prev = prevStored ? JSON.parse(prevStored) : null;

      if (JSON.stringify(prev) !== JSON.stringify(location.state)) {
        sessionStorage.setItem(
          "openIncidentsWmrcPayload",
          JSON.stringify(location.state)
        );
        setPayload(location.state);
        setFormattedPayload(formatPayloadDates(location.state));
      }
    } else if (!payload) {
      // fallback to URL params
      const params = Object.fromEntries([...searchParams]);
      if (Object.keys(params).length) {
        sessionStorage.setItem("openIncidentsWmrcPayload", JSON.stringify(params));
        setPayload(params);
        setFormattedPayload(formatPayloadDates(params));
      }
    }
  }, [location.state, searchParams]);

    const getOpenIncidentsWmrcReport = useCallback(async () => {
        let apiQueryParams = {
            per_page: tableOptions?.rowPerPages,
            page: tableOptions?.activePage + 1,
            export: 'N',
        }

        if (sort?.columnName && sort.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort?.columnName,
            }
        }

        const response = await fetchIncidentAnalysisData({
            ...formattedPayload,
            ...apiQueryParams,
            export: 'N',
        })
        return response
    }, [sort, tableOptions, formattedPayload])

    const { isLoading, isFetching, data, isError, error } = useQuery(
        ['fetch-open-incidents-wmrc', tableOptions, sort, formattedPayload],
        getOpenIncidentsWmrcReport,
        {
            enabled: !!formattedPayload,
            retry: (failureCount, error) => {
                const status = error?.response?.status
                return failureCount < 1 && status >= 400 && status < 600
            },
            retryDelay: 1000,
        }
    )

    const handlePageClick = (event) =>
        setTableOptions((prev) => ({ ...prev, activePage: event.selected }))
    const handleRowsPerPage = (e) =>
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e.target.value,
            activePage: 0,
        }))
    const handleColumnSort = (column, sortDirection) =>
        setSort({ columnName: column?.sortField, order: sortDirection })

    const downloadPDF = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...formattedPayload,
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            if (!response?.data || typeof response.data !== 'string') {
                toasters({
                    type: 'error',
                    message: 'No data available for PDF export',
                })
                return
            }

            const csvLines = response.data.trim().split('\n')
            if (csvLines.length <= 1) {
                toasters({
                    type: 'error',
                    message: 'No data available for PDF export',
                })
                return
            }

            const rows = csvLines
                .slice(1)
                .map((line) =>
                    line.split(',').map((val) => val.replace(/^"|"$/g, ''))
                )

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            })

            doc.setFontSize(18)
            doc.text('Open Incidents WMRC Report', 14, 18)

            doc.setFontSize(11)
            doc.text(
                `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
                14,
                26
            )

            doc.autoTable({
                head: [['WMRC User ID', 'WMRC User Name', 'Open Incidents']],
                body: rows,
                startY: 34,
                theme: 'grid',
                headStyles: {
                    fillColor: [0, 81, 242],
                    textColor: 255,
                    fontSize: 9,
                },
                bodyStyles: { fontSize: 9 },
            })

            doc.save('open-incidents-wmrc-report.pdf')
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message || 'Failed to generate PDF',
            })
        }
    }

    const ExcelOpenIncidentsWmrcReport = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...formattedPayload,
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            if (!response?.data || typeof response.data !== 'string') {
                toasters({ type: 'error', message: 'No data found to export' })
                return
            }

            const blob = new Blob([response.data], {
                type: 'text/csv;charset=utf-8;',
            })

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'open-incidents-wmrc-report.csv'
            link.click()
            URL.revokeObjectURL(url)
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Error downloading report',
            })
        }
    }

    const pageCount = Math.ceil((data?.total || 0) / tableOptions.rowPerPages)
    sessionStorage.clear()

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <OpenIncidentsWmrcReportTable
                    isLoading={isLoading || isFetching}
                    data={data?.data || []}
                    dates={formattedPayload}
                    totaldata={data?.total}
                    tableOptions={tableOptions}
                    handlePageClick={handlePageClick}
                    handleRowsPerPage={handleRowsPerPage}
                    handleColumnSort={handleColumnSort}
                    downloadPDF={downloadPDF}
                    downloadExcel={ExcelOpenIncidentsWmrcReport}
                    pageCount={pageCount}
                    reportData={formattedPayload}
                    reportCode={payload?.code}
                    reportTableData={data}
                    generatePDFFunction={downloadPDF}
                />
            )}
        </>
    )
}

export default OpenIncidentsWmrcReport
